/**
 * Workflow 自动化 — 手动触发页面
 * 代替 pg_cron 的人工触发版：点击按钮 + 进度条 + 结果预览
 */
import { useState } from "react";
import { useNotify, useRedirect } from "ra-core";
import {
  AlertCircle, CheckCircle, Clock, RefreshCw, Zap,
  DollarSign, FileText, ShoppingBag, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";

type JobStatus = "idle" | "running" | "done" | "error";

type WorkflowResult = {
  count: number;
  details?: { id: number; label: string; value: string }[];
};

const ProgressBar = ({ running, done }: { running: boolean; done: boolean }) => (
  <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden mt-2">
    {running ? (
      <div className="h-full bg-primary animate-pulse w-3/4 rounded-full transition-all" />
    ) : done ? (
      <div className="h-full bg-green-500 w-full rounded-full" />
    ) : null}
  </div>
);

type JobCardProps = {
  icon: React.ElementType;
  title: string;
  description: string;
  buttonLabel: string;
  onRun: () => Promise<WorkflowResult>;
  color: string;
};

const JobCard = ({ icon: Icon, title, description, buttonLabel, onRun, color }: JobCardProps) => {
  const notify = useNotify();
  const [status, setStatus] = useState<JobStatus>("idle");
  const [result, setResult] = useState<WorkflowResult | null>(null);

  const handleRun = async () => {
    setStatus("running");
    setResult(null);
    try {
      const r = await onRun();
      setResult(r);
      setStatus("done");
      notify(`${title}: 处理了 ${r.count} 条记录`, { type: "success" });
    } catch (e: any) {
      setStatus("error");
      notify(`${title} 失败: ${e.message}`, { type: "error" });
    }
  };

  return (
    <div className="bg-card border rounded-xl p-5 space-y-3">
      <div className="flex items-start gap-3">
        <div className={`rounded-lg p-2.5 shrink-0 ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm">{title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>

      <ProgressBar running={status === "running"} done={status === "done"} />

      {status === "done" && result && (
        <div className="text-xs space-y-1">
          <div className="flex items-center gap-1.5 text-green-600 font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            处理了 {result.count} 条记录
          </div>
          {result.details && result.details.length > 0 && (
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {result.details.map(d => (
                <div key={d.id} className="flex justify-between px-2 py-1 bg-muted/50 rounded text-xs">
                  <span className="text-muted-foreground truncate">{d.label}</span>
                  <span className="font-medium ml-2 shrink-0">{d.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {status === "error" && (
        <div className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="w-3.5 h-3.5" />
          执行失败，请重试
        </div>
      )}

      <Button size="sm" variant={status === "done" ? "outline" : "default"}
        disabled={status === "running"} onClick={handleRun} className="w-full">
        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${status === "running" ? "animate-spin" : ""}`} />
        {status === "running" ? "执行中..." : status === "done" ? "重新执行" : buttonLabel}
      </Button>
    </div>
  );
};

const WorkflowPage = () => {
  const supabase = getSupabaseClient();
  const redirect = useRedirect();

  const checkOverdueAR = async (): Promise<WorkflowResult> => {
    const { data, error } = await supabase.rpc("mark_overdue_ar_records");
    if (error) throw error;
    return { count: data ?? 0 };
  };

  const checkStaleQuotations = async (): Promise<WorkflowResult> => {
    const { data, error } = await supabase.rpc("check_stale_quotations");
    if (error) throw error;
    const rows = (data ?? []) as { quotation_id: number; days_since_sent: number }[];
    return {
      count: rows.length,
      details: rows.map(r => ({
        id: r.quotation_id,
        label: `报价单 QUO-${String(r.quotation_id).padStart(4, "0")}`,
        value: `已发出 ${r.days_since_sent}d 未回复`,
      })),
    };
  };

  const checkReorderCandidates = async (): Promise<WorkflowResult> => {
    const { data, error } = await supabase.rpc("check_reorder_candidates");
    if (error) throw error;
    const rows = (data ?? []) as { contact_id: number; last_order_id: number; days_since_completion: number }[];
    // Fetch contact names
    if (rows.length === 0) return { count: 0 };
    const ids = rows.map(r => r.contact_id);
    const { data: contacts } = await supabase
      .from("contacts")
      .select("id, first_name, last_name")
      .in("id", ids);
    const nameMap: Record<number, string> = {};
    (contacts ?? []).forEach((c: any) => { nameMap[c.id] = `${c.first_name} ${c.last_name}`; });
    return {
      count: rows.length,
      details: rows.map(r => ({
        id: r.contact_id,
        label: nameMap[r.contact_id] ?? `联系人 ${r.contact_id}`,
        value: `上次完成 ${r.days_since_completion}d 前`,
      })),
    };
  };

  const syncVIPLevels = async (): Promise<WorkflowResult> => {
    const { data, error } = await supabase.rpc("recalculate_all_vip_levels");
    if (error) throw error;
    return { count: data ?? 0 };
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Zap className="w-6 h-6" /> 工作流自动化
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          手动触发业务规则检查。点击按钮，查看进度和结果。
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <JobCard
          icon={DollarSign}
          title="应收账款逾期检查"
          description={'将所有已超过到期日且状态为"待收"的 AR 记录标记为"逾期"，并统计数量。'}
          buttonLabel="立即检查"
          onRun={checkOverdueAR}
          color="bg-red-50 text-red-600"
        />

        <JobCard
          icon={FileText}
          title="待跟进报价单"
          description="扫描发出超过 7 天未收到客户确认的报价单，列出需要主动跟进的清单。"
          buttonLabel="扫描报价单"
          onRun={checkStaleQuotations}
          color="bg-orange-50 text-orange-600"
        />

        <JobCard
          icon={ShoppingBag}
          title="复购提醒候选"
          description="找出最后一次订单完成超过 90 天的客户，便于销售主动联系促进复购。"
          buttonLabel="查找候选客户"
          onRun={checkReorderCandidates}
          color="bg-purple-50 text-purple-600"
        />

        <JobCard
          icon={Star}
          title="重算 VIP 等级"
          description="根据最新 VIP 门槛配置，重新计算所有联系人的 VIP 级别。适用于调整门槛后使用。"
          buttonLabel="重新计算 VIP"
          onRun={syncVIPLevels}
          color="bg-yellow-50 text-yellow-600"
        />
      </div>

      <div className="bg-muted/30 border rounded-lg p-4 text-xs text-muted-foreground space-y-1">
        <div className="flex items-center gap-1.5 font-medium text-foreground">
          <Clock className="w-3.5 h-3.5" />
          关于定时任务
        </div>
        <p>当前为手动触发模式。如需自动化，可在 Supabase 控制台开启 pg_cron 扩展，配置定时执行上述函数（推荐每日 UTC 06:00 执行）。</p>
      </div>
    </div>
  );
};

export default WorkflowPage;
